import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { TrafficInfoFilterItem, TrafficInfoResponse } from './traffic-info-types';
import { DropdownItemProps } from '../dropdown-widget';
import { ComboBoxOptions } from '../../components/controls/combo-box/combo-box';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export function getTypeByLineName(lineName: string): TrafficInfoFilterItem['type'] | undefined {
  if (lineName.startsWith('U-Bahn ')) {
    return 'subway';
  }
  if (lineName.startsWith('Niederflurstrab ')) {
    return 'tram';
  }
  if (lineName.startsWith('Niederflurbus ') || lineName.startsWith('SB ')) {
    return 'bus';
  }

  return undefined;
}

export function getFilterByLineName(
  lineName: string,
  filters: TrafficInfoFilterItem[]
): TrafficInfoFilterItem | undefined {
  const lineType = getTypeByLineName(lineName);
  return filters.find((trafficInfo) => trafficInfo.type === lineType);
}

export function buildLineComboBoxList(
  info: TrafficInfoResponse['infos'],
  filters: TrafficInfoFilterItem[]
): ComboBoxOptions[] {
  const result: DropdownItemProps[] = [];
  const activeFilters = filters.filter((filter) => filter.active);

  info.forEach((item) => {
    item.affectedLines.forEach((line) => {
      if (!line.number || result.find((l) => l.label === line.number)) {
        return;
      }

      if (activeFilters.length) {
        const lineType = getTypeByLineName(line.name);
        if (!lineType || !activeFilters.find((filter) => filter.type === lineType)) {
          return;
        }
      }

      result.push({
        value: line.number,
        label: line.number,
      });
    });
  });

  result.sort((a, b) => a.label.localeCompare(b.label));
  return result;
}

export function filterListByType(
  list: TrafficInfoResponse['infos'],
  filters: TrafficInfoFilterItem[]
): TrafficInfoResponse['infos'] {
  const activeTypes = filters.filter((filter) => filter.active).map((filter) => filter.type);
  if (activeTypes.length === 0) {
    return list;
  }

  return list.filter((infoEntry) => {
    const typesOfInfoEntry = infoEntry.affectedLines.map((line) => getTypeByLineName(line.name));
    if (typesOfInfoEntry.length === 0) {
      return true;
    }

    // At least *one type within the info entry* is part of the list of the *types of the activated filters*
    return typesOfInfoEntry.some((type) => type && activeTypes.includes(type));
  });
}

export function filterListByLineNumber(
  list: TrafficInfoResponse['infos'],
  lineNumber: string | undefined
): TrafficInfoResponse['infos'] {
  if (!lineNumber) {
    return list;
  }

  return list.filter((info) => {
    return info.affectedLines.some((line) => line.number === lineNumber);
  });
}

export function filterListByTimeInterval(
  list: TrafficInfoResponse['infos'],
  dateFilter: string | undefined
): TrafficInfoResponse['infos'] {
  if (!dateFilter) {
    return list;
  }

  return list.filter((info) => {
    const fromDate = info.fromDate.substring(0, 10);
    const untilDate = info.untilDate?.substring(0, 10);
    const fromDateOk = fromDate.localeCompare(dateFilter) <= 0;
    const untilDateOk = !untilDate || untilDate.localeCompare(dateFilter) >= 0;
    return fromDateOk && untilDateOk;
  });
}

export function sortListByFromDate(list: TrafficInfoResponse['infos']): TrafficInfoResponse['infos'] {
  return list.sort((a, b) => {
    return a.fromDate.localeCompare(b.fromDate);
  });
}

export function cleanupHtmlBody(body: string): string {
  // Remove unnecessary empty new lines at the end
  return body.replace(/<div[^>]*>&nbsp;<\/div>\s*$/g, '');
}
